import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  CircleAlert,
  CircleDollarSign,
  FolderOpen,
  Home,
  LogOut,
  MessageCircle,
  MoreHorizontal,
  PackageCheck,
  RefreshCw,
  Sparkles
} from 'lucide-react';
import { FormEvent, lazy, Suspense, useEffect, useMemo, useState } from 'react';
import {
  portalGetNotifications,
  portalStorage,
  requestPortalLogin,
  requestPortalProject,
  type PortalLoginResponse,
  type PortalProjectDetail
} from '@altios/api-client';
import { ActionText, Button, Surface, ThemeToggle } from '@altios/ui';
import { currentPhase, phaseTone, selectionComplete, type PortalMoneySection, type PortalView } from './lib/portalModel';
import { getPortalAttention } from './lib/portalAttention';
import { usePortalLiveProject } from './hooks/usePortalLiveProject';
import { PortalFeedback } from './components/PortalFeedback';

const PortalHome = lazy(() => import('./components/PortalHome').then((module) => ({ default: module.PortalHome })));
const PortalSelections = lazy(() => import('./components/PortalSelections').then((module) => ({ default: module.PortalSelections })));
const PortalMoneyHub = lazy(() => import('./components/PortalMoneyHub').then((module) => ({ default: module.PortalMoneyHub })));
const PortalMessages = lazy(() => import('./components/PortalMessages').then((module) => ({ default: module.PortalMessages })));
const PortalSchedule = lazy(() => import('./components/PortalSchedule').then((module) => ({ default: module.PortalSchedule })));
const PortalFiles = lazy(() => import('./components/PortalFiles').then((module) => ({ default: module.PortalFiles })));
const PortalProgress = lazy(() => import('./components/PortalProgress').then((module) => ({ default: module.PortalProgress })));
const PortalMore = lazy(() => import('./components/PortalMore').then((module) => ({ default: module.PortalMore })));

function PortalSectionFallback() {
  return <div className="cp-section-loading" role="status"><span className="cp-section-loading__pulse" />Loading project viewÃ¢â‚¬Â¦</div>;
}

const PORTAL_VIEWS: PortalView[] = ['home', 'selections', 'money', 'messages', 'schedule', 'files', 'progress', 'more'];

function normalizeMoneySection(value: string | null): PortalMoneySection {
  return value === 'estimates' || value === 'agreements' || value === 'changes' || value === 'payments' ? value : 'overview';
}

function normalizeView(value: string | null): PortalView | null {
  if (value === 'contracts' || value === 'change-orders' || value === 'payments' || value === 'estimates') return 'money';
  return PORTAL_VIEWS.includes(value as PortalView) ? value as PortalView : null;
}

function portalLocationState(): { projectId: string; view: PortalView; moneySection: PortalMoneySection } {
  const params = new URLSearchParams(window.location.search);
  const rawView = params.get('view');
  const urlView = normalizeView(rawView);
  const stored = window.sessionStorage.getItem(portalStorage.activeSection);
  const storedMoney = window.sessionStorage.getItem(portalStorage.moneySection);
  const moneySection = rawView === 'estimates' ? 'estimates' : rawView === 'contracts' ? 'agreements'
    : rawView === 'change-orders' ? 'changes'
      : rawView === 'payments' ? 'payments'
        : normalizeMoneySection(params.get('money') || storedMoney);
  return {
    projectId: params.get('project') || '',
    view: urlView || normalizeView(stored) || 'home',
    moneySection
  };
}

function writePortalLocation(projectId: string | null, view: PortalView, moneySection: PortalMoneySection, mode: 'push' | 'replace' = 'push') {
  const url = new URL(window.location.href);
  if (projectId) url.searchParams.set('project', projectId); else url.searchParams.delete('project');
  url.searchParams.set('view', view);
  if (view === 'money' && moneySection !== 'overview') url.searchParams.set('money', moneySection); else url.searchParams.delete('money');
  window.history[mode === 'replace' ? 'replaceState' : 'pushState']({ altiosPortal: true, projectId, view, moneySection }, '', url);
}

function restoredView(): { view: PortalView; moneySection: PortalMoneySection } {
  const state = portalLocationState();
  return { view: state.view, moneySection: state.moneySection };
}

export function PortalApp() {
  const restored = useMemo(restoredView, []);
  const [email, setEmail] = useState(() => window.localStorage.getItem(portalStorage.email) || '');
  const [session, setSession] = useState<PortalLoginResponse | null>(null);
  const [detail, setDetail] = useState<PortalProjectDetail | null>(null);
  const [view, setView] = useState<PortalView>(restored.view);
  const [moneySection, setMoneySection] = useState<PortalMoneySection>(restored.moneySection);
  const [busy, setBusy] = useState(false);
  const [projectBusy, setProjectBusy] = useState(false);
  const [error, setError] = useState('');
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [pendingEstimateNotice, setPendingEstimateNotice] = useState(false);
  usePortalLiveProject({
    projectId: detail?.project.id,
    email,
    enabled: Boolean(session && detail),
    onProject: setDetail
  });

  async function openProject(projectId: string, nextView: PortalView = view, historyMode: 'push' | 'replace' | 'none' = 'push') {
    if (!projectId || !email.trim()) return;
    setProjectBusy(true); setError('');
    try {
      const data = await requestPortalProject(projectId, email.trim());
      window.sessionStorage.setItem(portalStorage.projectId, projectId);
      window.sessionStorage.setItem(portalStorage.activeSection, nextView);
      setDetail(data); setView(nextView);
      if (historyMode !== 'none') writePortalLocation(projectId, nextView, moneySection, historyMode);
    } catch (projectError) {
      setError(projectError instanceof Error ? projectError.message : 'Unable to open this project.');
    } finally { setProjectBusy(false); }
  }

  async function openProjectWithEmail(projectId: string, viewerEmail: string, nextView: PortalView, historyMode: 'push' | 'replace' | 'none' = 'replace') {
    setProjectBusy(true);
    try {
      const data = await requestPortalProject(projectId, viewerEmail);
      window.sessionStorage.setItem(portalStorage.projectId, projectId);
      window.sessionStorage.setItem(portalStorage.activeSection, nextView);
      setDetail(data); setView(nextView);
      if (historyMode !== 'none') writePortalLocation(projectId, nextView, moneySection, historyMode);
    } finally { setProjectBusy(false); }
  }

  async function login(viewerEmail: string) {
    const normalizedEmail = viewerEmail.trim();
    if (!normalizedEmail) return;
    setBusy(true); setError('');
    try {
      const data = await requestPortalLogin(normalizedEmail);
      window.localStorage.setItem(portalStorage.email, normalizedEmail);
      window.sessionStorage.setItem(portalStorage.sessionActive, '1');
      setEmail(normalizedEmail); setSession(data);
      const locationState = portalLocationState();
      const storedProjectId = window.sessionStorage.getItem(portalStorage.projectId);
      const availableProjectIds = new Set([
        ...data.projects.map((project) => project.id),
        ...(data.project?.id ? [data.project.id] : [])
      ]);
      const requestedProjectId = locationState.projectId && availableProjectIds.has(locationState.projectId) ? locationState.projectId : '';
      const projectId = requestedProjectId || (storedProjectId && availableProjectIds.has(storedProjectId)
        ? storedProjectId
        : data.project?.id || (data.projects.length === 1 ? data.projects[0]?.id || '' : ''));
      setMoneySection(locationState.moneySection);
      if (projectId) await openProjectWithEmail(projectId, normalizedEmail, locationState.view, 'replace');
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : 'Unable to open the customer portal.');
      window.sessionStorage.removeItem(portalStorage.sessionActive);
    } finally { setBusy(false); }
  }

  useEffect(() => {
    if (email && window.sessionStorage.getItem(portalStorage.sessionActive) === '1') void login(email);
    // Restore the existing portal session once at startup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onPopState = () => {
      const state = portalLocationState();
      setView(state.view);
      setMoneySection(state.moneySection);
      window.sessionStorage.setItem(portalStorage.activeSection, state.view);
      window.sessionStorage.setItem(portalStorage.moneySection, state.moneySection);
      if (!session || !email) return;
      if (!state.projectId) {
        if (session.projects.length > 1) setDetail(null);
        return;
      }
      if (detail?.project.id !== state.projectId) void openProject(state.projectId, state.view, 'none');
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [detail?.project.id, email, session]);

  useEffect(() => {
    if (!detail || !email) { setUnreadMessages(0); setPendingEstimateNotice(false); return; }
    let cancelled = false;
    portalGetNotifications(detail.project.id, email).then((result) => {
      if (cancelled) return;
      setUnreadMessages(result.notifications.filter((item) => item.section === 'messages' && Number(item.is_read) === 0).length);
      setPendingEstimateNotice(result.notifications.some((item) =>
        item.event_type === 'EstimateApprovalRequested' && item.entity_type === 'estimate' && Number(item.action_required) === 1
      ));
    }).catch(() => { if (!cancelled) setUnreadMessages(0); });
    return () => { cancelled = true; };
  }, [detail, email]);

  function submit(event: FormEvent) { event.preventDefault(); void login(email); }

  function signOut() {
    window.localStorage.removeItem(portalStorage.email);
    window.sessionStorage.removeItem(portalStorage.sessionActive);
    window.sessionStorage.removeItem(portalStorage.projectId);
    window.sessionStorage.removeItem(portalStorage.activeSection);
    window.sessionStorage.removeItem(portalStorage.moneySection);
    setSession(null); setDetail(null); setEmail(''); setError(''); setView('home'); setMoneySection('overview'); setUnreadMessages(0); setPendingEstimateNotice(false);
    writePortalLocation(null, 'home', 'overview', 'replace');
  }

  function navigate(next: PortalView, options?: { moneySection?: PortalMoneySection }) {
    const nextMoney = options?.moneySection || moneySection;
    if (options?.moneySection) {
      setMoneySection(options.moneySection);
      window.sessionStorage.setItem(portalStorage.moneySection, options.moneySection);
    }
    setView(next);
    window.sessionStorage.setItem(portalStorage.activeSection, next);
    if (detail?.project.id) writePortalLocation(detail.project.id, next, nextMoney, 'push');
  }

  function changeMoneySection(next: PortalMoneySection) {
    setMoneySection(next);
    window.sessionStorage.setItem(portalStorage.moneySection, next);
    if (detail?.project.id) writePortalLocation(detail.project.id, 'money', next, 'push');
  }

  const selectionRemaining = useMemo(() => detail ? detail.selections.filter((selection) => !selectionComplete(selection)).length : 0, [detail]);
  const attention = useMemo(
    () => detail ? getPortalAttention(detail, unreadMessages, pendingEstimateNotice) : null,
    [detail, unreadMessages, pendingEstimateNotice]
  );
  const moneyAttention = attention?.moneyCount || 0;

  if (session) {
    const multipleProjects = session.projects.length > 1;
    if (!detail) {
      return (
        <div className="cp-project-picker-shell">
          <header className="cp-simple-header"><div className="cp-brand"><img src="/logo.png" alt="Altios Builders" /><div><strong>Altios Builders</strong><span>Customer Portal</span></div></div><div><ThemeToggle compact /><ActionText tone="neutral" icon={<LogOut size={15} />} onClick={signOut}>Sign out</ActionText></div></header>
          <main className="cp-project-picker">
            <div className="cp-picker-heading"><span className="cp-eyebrow">Your projects</span><h1>Choose a project</h1><p>Open the project you want to review. Each project keeps its own decisions, money, messages, schedule and files.</p></div>
            {error ? <div className="cp-error"><CircleAlert size={17} />{error}</div> : null}
            <div className="cp-project-grid">{session.projects.map((project) => <Surface className="cp-project-card" key={project.id}><div><span>Altios project</span><strong>{project.name || 'Project'}</strong><small>{[project.first_name, project.last_name].filter(Boolean).join(' ') || project.customer_email || ''}</small></div><Button tone="positive" trailingIcon={<ArrowRight size={16} />} disabled={projectBusy} onClick={() => void openProject(project.id, 'home')}>{projectBusy ? 'OpeningÃ¢â‚¬Â¦' : 'Open project'}</Button></Surface>)}</div>
          </main>
        </div>
      );
    }

    const phase = currentPhase(detail);
    const tone = phaseTone(phase);
    const moreActive = view === 'more' || view === 'schedule' || view === 'files' || view === 'progress';
    const desktopNav = [
      { key: 'home' as const, label: 'Home', icon: Home, badge: 0, needsAction: Boolean(attention?.home) },
      { key: 'selections' as const, label: 'Selections', icon: PackageCheck, badge: selectionRemaining, needsAction: Boolean(attention?.selections) },
      { key: 'money' as const, label: 'Payments & Agreements', icon: CircleDollarSign, badge: moneyAttention, needsAction: Boolean(attention?.money) },
      { key: 'messages' as const, label: 'Messages', icon: MessageCircle, badge: unreadMessages, needsAction: Boolean(attention?.messages) },
      { key: 'schedule' as const, label: 'Schedule', icon: CalendarDays, badge: 0, needsAction: Boolean(attention?.schedule) },
      { key: 'files' as const, label: 'Files', icon: FolderOpen, badge: 0, needsAction: false }
    ];

    return (
      <div className="cp-shell">
        <header className="cp-topbar">
          <div className="cp-brand"><img src="/logo.png" alt="Altios Builders" /><div><strong>Altios Builders</strong><span>Project Concierge</span></div></div>
          <div className="cp-project-identity">
            {multipleProjects ? <button type="button" onClick={() => { setDetail(null); window.sessionStorage.removeItem(portalStorage.projectId); writePortalLocation(null, 'home', 'overview', 'push'); }}><ArrowLeft size={14} /> All projects</button> : null}
            <div><strong>{detail.project.name || 'Your project'}</strong><span className={`cp-phase-label cp-phase-label--${tone}`}>{phase}</span></div>
          </div>
          <div className="cp-topbar-actions"><ActionText tone="link" onClick={() => navigate('messages')}>Need help?</ActionText><ThemeToggle compact /><ActionText tone="neutral" icon={<LogOut size={15} />} onClick={signOut}>Sign out</ActionText></div>
        </header>

        <nav className="cp-desktop-nav" aria-label="Customer Portal navigation">
          {desktopNav.map((item) => { const Icon = item.icon; return <button type="button" key={item.key} className={`${view === item.key ? 'is-active ' : ''}${item.needsAction ? 'cp-nav-attention' : 'cp-nav-good'}`} aria-label={`${item.label}${item.needsAction ? ' — action needed' : ' — no action needed'}`} onClick={() => navigate(item.key)}><Icon size={16} /><span>{item.label}</span>{item.badge ? <b>{item.badge}</b> : null}</button>; })}
        </nav>

        <main className="cp-main">
          {projectBusy ? <div className="cp-refreshing"><RefreshCw className="spin" size={17} /> Refreshing projectÃ¢â‚¬Â¦</div> : null}
          {error ? <div className="cp-error"><CircleAlert size={17} />{error}</div> : null}
          <Suspense fallback={<PortalSectionFallback />}>
            {view === 'home' ? <PortalHome detail={detail} email={email} onNavigate={navigate} onRefresh={() => openProject(detail.project.id, 'home', 'none')} />
              : view === 'selections' ? <PortalSelections detail={detail} email={email} onRefresh={() => openProject(detail.project.id, 'selections')} />
                : view === 'money' ? <PortalMoneyHub detail={detail} email={email} isAdmin={Boolean(session.isAdmin)} section={moneySection} attention={attention?.moneySections} pendingChangeCount={attention?.changeCount || 0} onSectionChange={changeMoneySection} onRefresh={() => openProject(detail.project.id, 'money')} />
                  : view === 'messages' ? <PortalMessages detail={detail} email={email} onRefresh={() => openProject(detail.project.id, 'messages')} />
                    : view === 'schedule' ? <PortalSchedule detail={detail} email={email} onRefresh={() => openProject(detail.project.id, 'schedule')} />
                      : view === 'files' ? <PortalFiles detail={detail} email={email} />
                        : view === 'progress' ? <PortalProgress detail={detail} email={email} />
                          : <PortalMore onNavigate={navigate} onSignOut={signOut} scheduleNeedsAction={attention?.schedule} />}
          </Suspense>
          <PortalFeedback projectId={detail.project.id} email={email} view={view} moneySection={moneySection} />
        </main>

        <nav className="cp-mobile-nav" aria-label="Mobile customer navigation">
          <button type="button" className={`${view === 'home' ? 'is-active ' : ''}${attention?.home ? 'cp-nav-attention' : 'cp-nav-good'}`} onClick={() => navigate('home')}><Home size={20} /><span>Home</span></button>
          <button type="button" className={`${view === 'selections' ? 'is-active ' : ''}${attention?.selections ? 'cp-nav-attention' : 'cp-nav-good'}`} onClick={() => navigate('selections')}><PackageCheck size={20} /><span>Selections</span>{selectionRemaining ? <b>{selectionRemaining}</b> : null}</button>
          <button type="button" className={`${view === 'money' ? 'is-active ' : ''}${attention?.money ? 'cp-nav-attention' : 'cp-nav-good'}`} onClick={() => navigate('money')}><CircleDollarSign size={20} /><span>Payments</span>{moneyAttention ? <b>{moneyAttention}</b> : null}</button>
          <button type="button" className={`${view === 'messages' ? 'is-active ' : ''}${attention?.messages ? 'cp-nav-attention' : 'cp-nav-good'}`} onClick={() => navigate('messages')}><MessageCircle size={20} /><span>Messages</span>{unreadMessages ? <b>{unreadMessages}</b> : null}</button>
          <button type="button" className={`${moreActive ? 'is-active ' : ''}${attention?.schedule ? 'cp-nav-attention' : 'cp-nav-good'}`} onClick={() => navigate('more')}><MoreHorizontal size={20} /><span>More</span></button>
        </nav>
      </div>
    );
  }

  return (
    <div className="cp-login">
      <section className="cp-login-brand"><img src="/logo.png" alt="Altios Builders" /><div><span>Altios Builders</span><h1>Your project, without the noise.</h1><p>Selections, payments, agreements, messages, schedule and files in one guided customer experience.</p></div><div className="cp-ai-note"><Sparkles size={18} /><span>AI assistance appears only where it helps you understand or make a project decision.</span></div></section>
      <main className="cp-login-panel"><div className="cp-login-theme"><ThemeToggle /></div><Surface className="cp-login-card"><div><span className="cp-eyebrow">Customer Portal</span><h2>Open your project</h2><p>Use the email address associated with your Altios project.</p></div><form onSubmit={submit}><label htmlFor="portal-email">Email address</label><input id="portal-email" type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@example.com" required /><Button type="submit" tone="positive" size="lg" disabled={busy} trailingIcon={<ArrowRight size={17} />}>{busy ? 'Opening projectÃ¢â‚¬Â¦' : 'Open my project'}</Button></form>{error ? <div className="cp-error" role="alert"><CircleAlert size={17} /><span>{error}</span></div> : null}</Surface></main>
    </div>
  );
}
